import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, CheckCircle, AlertTriangle, HardDrive } from 'lucide-react';
import { X, Upload, FileText, User } from './icons';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../services/api';

export default function UploadHistoricoModal({ isOpen, onClose, onUploadComplete }) {
    const [file, setFile] = useState(null);
    const [usuarios, setUsuarios] = useState([]);
    const [activos, setActivos] = useState([]);
    const [searchTermUser, setSearchTermUser] = useState('');
    const [searchTermActivo, setSearchTermActivo] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedActivo, setSelectedActivo] = useState(null);

    // Si el usuario no está en la base de datos (ej. extrabajador)
    const [isCustomUser, setIsCustomUser] = useState(false);
    const [customUserName, setCustomUserName] = useState('');

    const [fechaFirma, setFechaFirma] = useState(() => {
        const now = new Date();
        return `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [uploading, setUploading] = useState(false);

    useEffect(() => {
        if (isOpen) {
            fetchUsuarios();
            fetchActivos();
            resetState();
        }
    }, [isOpen]);

    const resetState = () => {
        setFile(null);
        setSearchTermUser('');
        setSearchTermActivo('');
        setSelectedUser(null);
        setSelectedActivo(null);
        setIsCustomUser(false);
        setCustomUserName('');
        const now = new Date();
        setFechaFirma(`${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`);
        setError('');
    };

    const fetchUsuarios = async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/usuarios');
            setUsuarios(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching usuarios:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchActivos = async () => {
        try {
            // /activos devuelve paginación server-side ({ items, pagination, stats }),
            // no un array plano -- de ahi salia "activos.filter is not a function"
            // en cuanto la respuesta llegaba y tumbaba el modal completo.
            const response = await api.get('/activos', { params: { pageSize: 500 } });
            setActivos(Array.isArray(response.data?.items) ? response.data.items : []);
        } catch (err) {
            console.error('Error fetching activos:', err);
        }
    };

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'application/pdf') {
                setError('El archivo debe ser un PDF.');
                return;
            }
            if (selectedFile.size > 10 * 1024 * 1024) {
                setError('El archivo no debe exceder los 10MB.');
                return;
            }
            setFile(selectedFile);
            setError('');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!file) {
            setError('Debes seleccionar un archivo PDF.');
            return;
        }

        if (!isCustomUser && !selectedUser) {
            setError('Debes seleccionar un usuario de la lista o ingresar un nombre manual.');
            return;
        }

        if (isCustomUser && !customUserName.trim()) {
            setError('Debes ingresar el nombre del usuario histórico.');
            return;
        }

        if (!fechaFirma) {
            setError('Debes especificar la fecha del acta.');
            return;
        }

        try {
            setUploading(true);
            const formData = new FormData();
            formData.append('acta', file);

            if (!isCustomUser && selectedUser) {
                formData.append('usuarioId', selectedUser.id);
            } else if (isCustomUser) {
                formData.append('usuarioNombre', customUserName);
            }

            if (selectedActivo) {
                formData.append('activoId', selectedActivo.id);
            }

            // Convertir dd/mm/yyyy a yyyy-mm-dd para el backend
            const [dd, mm, yyyy] = fechaFirma.split('/');
            formData.append('fechaFirma', `${yyyy}-${mm}-${dd}`);

            await api.post('/actas/historica', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            onUploadComplete();
        } catch (err) {
            console.error('Error uploading acta:', err);
            setError(err.response?.data?.message || 'Error al subir el acta histórica');
        } finally {
            setUploading(false);
        }
    };

    const filteredUsers = usuarios.filter(u =>
        u.nombre?.toLowerCase().includes(searchTermUser.toLowerCase()) ||
        u.apellido?.toLowerCase().includes(searchTermUser.toLowerCase())
    ).slice(0, 5);

    const filteredActivos = activos.filter(a =>
        a.codigo?.toLowerCase().includes(searchTermActivo.toLowerCase()) ||
        a.nombreEquipo?.toLowerCase().includes(searchTermActivo.toLowerCase())
    ).slice(0, 5);

    if (!isOpen) return null;

    return createPortal(
        <AnimatePresence>
            <div className="fixed inset-0 z-[9999] overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:p-0">
                    <div className="fixed inset-0 transition-opacity" onClick={onClose}>
                        <div className="absolute inset-0 bg-gray-900 bg-opacity-75 dark:bg-opacity-90"></div>
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="relative z-10 inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-xl w-full"
                    >
                        <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                            <div className="flex justify-between items-center mb-5">
                                <h3 className="text-xl leading-6 font-semibold text-gray-900 dark:text-white flex items-center">
                                    <Upload className="w-6 h-6 mr-2 text-blue-600 dark:text-blue-400" />
                                    Subir Acta Histórica (PDF)
                                </h3>
                                <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                                    <X className="w-6 h-6" />
                                </button>
                            </div>

                            {error && (
                                <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500 text-red-700 dark:text-red-400">
                                    <div className="flex items-center">
                                        <AlertTriangle className="w-5 h-5 mr-2" />
                                        <p>{error}</p>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Zona de Drop del Archivo */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Archivo PDF
                                    </label>
                                    <div className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-lg ${file ? 'border-green-500 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-gray-600'
                                        }`}>
                                        <div className="space-y-1 text-center">
                                            {file ? (
                                                <div className="flex flex-col items-center">
                                                    <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
                                                    <p className="mt-2 text-sm font-semibold text-green-600 dark:text-green-400">
                                                        {file.name}
                                                    </p>
                                                    <button
                                                        type="button"
                                                        onClick={() => setFile(null)}
                                                        className="mt-2 text-xs text-red-500 hover:text-red-700 font-medium"
                                                    >
                                                        Cambiar archivo
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <FileText className="mx-auto h-12 w-12 text-gray-400" />
                                                    <div className="flex text-sm text-gray-600 dark:!text-gray-400 mt-2 justify-center">
                                                        <label className="relative cursor-pointer bg-blue-50 dark:!bg-gray-800 rounded-md font-medium text-blue-600 dark:!text-blue-400 hover:text-blue-500 focus-within:outline-none px-2 py-1">
                                                            <span>Haz click aquí para subir</span>
                                                            <input
                                                                type="file"
                                                                className="sr-only"
                                                                accept=".pdf"
                                                                onChange={handleFileChange}
                                                                required
                                                            />
                                                        </label>
                                                    </div>
                                                    <p className="text-xs text-gray-500">
                                                        Solo archivos PDF hasta 10MB
                                                    </p>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Selección de Usuario */}
                                <div>
                                    <div className="flex justify-between items-center mb-2">
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                            Usuario Propietario del Acta
                                        </label>
                                        <label className="flex items-center text-sm text-gray-600 dark:text-gray-400 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                className="mr-2 rounded border-gray-300"
                                                checked={isCustomUser}
                                                onChange={(e) => {
                                                    setIsCustomUser(e.target.checked);
                                                    setSelectedUser(null);
                                                    setSearchTermUser('');
                                                }}
                                            />
                                            El usuario ya no está en la empresa
                                        </label>
                                    </div>

                                    {isCustomUser ? (
                                        <div className="relative">
                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                <User className="h-5 w-5 text-gray-400" />
                                            </div>
                                            <input
                                                type="text"
                                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:!border-gray-600 rounded-lg bg-gray-50 dark:!bg-gray-700 text-gray-900 dark:!text-white sm:text-sm"
                                                placeholder="Ej. Daniel Gallegos"
                                                value={customUserName}
                                                onChange={(e) => setCustomUserName(e.target.value)}
                                                required={isCustomUser}
                                            />
                                        </div>
                                    ) : (
                                        <div className="relative">
                                            {selectedUser ? (
                                                <div className="flex items-center justify-between p-3 border border-blue-300 dark:border-blue-600 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                                                    <div className="flex items-center">
                                                        <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center mr-3">
                                                            <span className="text-blue-600 dark:text-blue-300 font-medium">
                                                                {selectedUser.nombre?.[0] || ''}{selectedUser.apellido?.[0] || ''}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                                {selectedUser.nombre} {selectedUser.apellido}
                                                            </p>
                                                            <p className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 truncate max-w-xs">{selectedUser.email}</p>
                                                        </div>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => setSelectedUser(null)}
                                                        className="text-gray-400 hover:text-red-500"
                                                    >
                                                        <X className="w-5 h-5" />
                                                    </button>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                        <User className="h-5 w-5 text-gray-400" />
                                                    </div>
                                                    <input
                                                        type="text"
                                                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:!border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-gray-50 dark:!bg-gray-700 text-gray-900 dark:!text-white sm:text-sm"
                                                        placeholder="Buscar empleado activo..."
                                                        value={searchTermUser}
                                                        onChange={(e) => setSearchTermUser(e.target.value)}
                                                    />

                                                    {searchTermUser && filteredUsers.length > 0 && (
                                                        <ul className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto sm:text-sm border border-gray-200 dark:border-gray-700">
                                                            {filteredUsers.map((user) => (
                                                                <li
                                                                    key={user.id}
                                                                    className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-4 py-2"
                                                                    onClick={() => {
                                                                        setSelectedUser(user);
                                                                        setSearchTermUser('');
                                                                    }}
                                                                >
                                                                    <div className="flex flex-col">
                                                                        <span className="font-medium text-gray-900 dark:text-white">{user.nombre} {user.apellido}</span>
                                                                        <span className="text-gray-500 dark:text-gray-400">{user.email || user.departamento}</span>
                                                                    </div>
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Selección Opcional de Activo */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Vincular a un Equipo (Opcional)
                                    </label>
                                    <div className="relative">
                                        {selectedActivo ? (
                                            <div className="flex items-center justify-between p-3 border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                <div className="flex items-center">
                                                    <HardDrive className="h-5 w-5 text-gray-500 mr-2" />
                                                    <div>
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {selectedActivo.codigo}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {selectedActivo.nombreEquipo || selectedActivo.categoria}
                                                        </p>
                                                    </div>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedActivo(null)}
                                                    className="text-gray-400 hover:text-red-500"
                                                >
                                                    <X className="w-5 h-5" />
                                                </button>
                                            </div>
                                        ) : (
                                            <>
                                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                    <HardDrive className="h-5 w-5 text-gray-400" />
                                                </div>
                                                <input
                                                    type="text"
                                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:!border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-gray-50 dark:!bg-gray-700 text-gray-900 dark:!text-white sm:text-sm"
                                                    placeholder="Buscar por código (ej. PC-001)..."
                                                    value={searchTermActivo}
                                                    onChange={(e) => setSearchTermActivo(e.target.value)}
                                                />

                                                {searchTermActivo && filteredActivos.length > 0 && (
                                                    <ul className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto sm:text-sm border border-gray-200 dark:border-gray-700 pb-2">
                                                        {filteredActivos.map((activo) => (
                                                            <li
                                                                key={activo.id}
                                                                className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 px-4 py-2"
                                                                onClick={() => {
                                                                    setSelectedActivo(activo);
                                                                    setSearchTermActivo('');
                                                                }}
                                                            >
                                                                <div className="flex justify-between">
                                                                    <span className="font-medium text-gray-900 dark:text-white">{activo.codigo}</span>
                                                                    <span className="text-gray-500 dark:text-gray-400">{activo.nombreEquipo || activo.categoria}</span>
                                                                </div>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </>
                                        )}
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">
                                        Útil si deseas que el acta sea visible en el historial específico del equipo.
                                    </p>
                                </div>

                                {/* Fecha */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        Fecha de Firma del Acta
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <Calendar className="h-5 w-5 text-gray-400" />
                                        </div>
                                        <input
                                            type="text"
                                            className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:!border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-gray-50 dark:!bg-gray-700 text-gray-900 dark:!text-white sm:text-sm"
                                            value={fechaFirma}
                                            onChange={(e) => {
                                                let val = e.target.value.replace(/[^0-9/]/g, '');
                                                // Auto-insert slashes
                                                if (val.length === 2 && fechaFirma.length === 1) val += '/';
                                                if (val.length === 5 && fechaFirma.length === 4) val += '/';
                                                if (val.length <= 10) setFechaFirma(val);
                                            }}
                                            placeholder="dd/mm/aaaa"
                                            maxLength={10}
                                            required
                                        />
                                    </div>
                                    <p className="mt-1 text-xs text-gray-500">Formato: día/mes/año (ej. 15/03/2025)</p>
                                </div>

                                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                                    <button
                                        type="submit"
                                        disabled={uploading}
                                        className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:col-start-2 sm:text-sm disabled:opacity-50"
                                    >
                                        {uploading ? 'Subiendo...' : 'Guardar Acta'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={uploading}
                                        className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:col-start-1 sm:text-sm disabled:opacity-50"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                </div>
            </div>
        </AnimatePresence>,
        document.body
    );
}
